import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/store/api';
import { isTargetOnline } from '@/utils/transferTargetFilter';
import { useQueueTransferOptions } from '@/hooks/useQueueTransferOptions';
import type { ConnectContact } from '@/types';
import type { CombinedTransferTargetDTO } from '@/types/agents';

export type { QueueTargetOption } from '@/hooks/useQueueTransferOptions';

export type TransferTargetOption = {
  value: string;
  label: string;
  type: 'spog_agent' | 'teams_user' | 'teams_group';
  displayName: string;
  statusInfo?: string;
  status?: string;
  online?: boolean;
  targetPhoneNumber?: string;
};

export function useTransferTargetOptions(
  activeContact: ConnectContact | undefined
) {
  const { t } = useTranslation();
  const [triggerGetTargets, { data: targetsData, isFetching: loadingTargets }] =
    api.useLazyGetCombinedTransferTargetsQuery();
  const { queueOptions, loadingQueues, refreshQueues } =
    useQueueTransferOptions(activeContact);

  useEffect(() => {
    void triggerGetTargets(undefined, false);
  }, [triggerGetTargets]);

  const getStatusInfo = useCallback(
    (target: CombinedTransferTargetDTO) => {
      if (target.type === 'spog_agent') {
        return target.statusInfo
          ? `\n${t('components.contactCenter.sinceTime', {
              time: new Date(target.statusInfo).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              }),
            })}`
          : '';
      }
      return '';
    },
    [t]
  );

  const agentTargetOptions = useMemo<TransferTargetOption[]>(() => {
    if (!targetsData?.items) return [];
    return targetsData.items
      .map(target => {
        const icon =
          target.type === 'spog_agent'
            ? '🎧'
            : target.type === 'teams_user'
              ? '👤'
              : '👥';
        return {
          value: target.id,
          label: `${icon} ${target.name}${getStatusInfo(target)}`,
          type: target.type,
          displayName: target.name,
          statusInfo: target.statusInfo,
          status: target.status,
          online: isTargetOnline(target),
          targetPhoneNumber: target.targetPhoneNumber,
        };
      })
      .sort((a, b) => Number(b.online === true) - Number(a.online === true));
  }, [targetsData, getStatusInfo]);

  /** Refetch agent/Teams targets (same as CallTransferPanel dropdown open). */
  const refreshTargets = useCallback(() => {
    void triggerGetTargets(undefined, false);
  }, [triggerGetTargets]);

  return {
    agentTargetOptions,
    queueOptions,
    loadingTargets,
    loadingQueues,
    refreshTargets,
    refreshQueues,
  };
}
